import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import HeroBanner from "./components/HeroBanner";
import ServicesOverview from "./components/ServicesOverview";
import WhyChooseUs from "./components/WhyChooseUs";
import Pricing from "./components/Pricing";
import BookingForm from "./components/BookingForm";
import Testimonials from "./components/Testimonials";
import Gallery from "./components/Gallery";
import SchemaOrg from "../../components/feature/SchemaOrg";

const siteUrl = import.meta.env.VITE_SITE_URL ?? "https://example.com";

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "HomeAndConstructionBusiness"],
  "@id": `${siteUrl}/#business`,
  name: "ZoroBabel Cleaning Services",
  url: siteUrl,
  logo: "https://static.readdy.ai/image/0bfa0af67c6b6be26198661c1a60759c/3d8ce18578c897b8381251a25aef549c.jpeg",
  image: "https://static.readdy.ai/image/0bfa0af67c6b6be26198661c1a60759c/3d8ce18578c897b8381251a25aef549c.jpeg",
  description:
    "ZoroBabel Cleaning Services offers professional residential and commercial cleaning in Miami, FL. Licensed, insured, and satisfaction guaranteed. Packages include Maintenance Cleaning, Full Cleaning, and Deep Cleaning.",
  telephone: "",
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Miami",
    addressRegion: "FL",
    addressCountry: "US",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 25.7617,
    longitude: -80.1918,
  },
  areaServed: [
    { "@type": "City", name: "Miami" },
    { "@type": "City", name: "Coral Gables" },
    { "@type": "City", name: "Brickell" },
    { "@type": "City", name: "Miami Beach" },
    { "@type": "City", name: "Doral" },
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Cleaning Services",
    itemListElement: [
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Basic – Maintenance Cleaning",
          description:
            "Ideal for homes that are already clean and need only upkeep. Covers general surface cleaning, sweeping, vacuuming, mopping, kitchen and bathroom cleaning, trash removal, and light organization.",
        },
        priceSpecification: {
          "@type": "PriceSpecification",
          price: "150",
          priceCurrency: "USD",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Standard – Full Cleaning",
          description:
            "For maintaining your home in great condition with more attention to detail. Includes everything in Basic plus detailed bathroom cleaning, appliance exteriors, doors and frames, baseboards, thorough dusting, and inside the microwave.",
        },
        priceSpecification: {
          "@type": "PriceSpecification",
          minPrice: "220",
          maxPrice: "250",
          priceCurrency: "USD",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Premium – Deep Cleaning",
          description:
            "Ideal for first-time cleanings or very dirty homes. Includes everything in Standard plus deep cleaning of the entire home, inside oven and refrigerator, interior windows, corners and hard-to-reach areas, removal of built-up grease, treatment of heavily soiled areas, and special focus on kitchen and bathrooms.",
        },
        priceSpecification: {
          "@type": "PriceSpecification",
          price: "300",
          priceCurrency: "USD",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Space Organization Add-On",
          description:
            "Professional space organization service available as an add-on to any cleaning package. Covers closets, kitchens, pantries, bathrooms, kids rooms, play areas, and storage spaces. Includes sorting, folding, space optimization, and functional reorganization.",
        },
        priceSpecification: {
          "@type": "PriceSpecification",
          minPrice: "40",
          maxPrice: "50",
          priceCurrency: "USD",
          unitText: "per hour",
        },
      },
    ],
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "5",
    reviewCount: "4",
    bestRating: "5",
    worstRating: "1",
  },
  review: [
    {
      "@type": "Review",
      author: { "@type": "Person", name: "Sarah Mitchell" },
      reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
      reviewBody:
        "ZoroBabel completely transformed my Coral Gables home. Every corner was spotless — highly recommend!",
    },
    {
      "@type": "Review",
      author: { "@type": "Person", name: "David Chen" },
      reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
      reviewBody:
        "As a busy professional in Brickell, ZoroBabel keeps my condo immaculate every week. Outstanding service!",
    },
    {
      "@type": "Review",
      author: { "@type": "Person", name: "Emma Rodriguez" },
      reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
      reviewBody:
        "Their eco-friendly products made all the difference. My Miami Beach apartment has never looked better!",
    },
    {
      "@type": "Review",
      author: { "@type": "Person", name: "James Wilson" },
      reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
      reviewBody:
        "ZoroBabel handled our move-out clean in Doral perfectly. Got our full deposit back — couldn't be happier.",
    },
  ],
  sameAs: [],
};

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${siteUrl}/#website`,
  url: siteUrl,
  name: "ZoroBabel Cleaning Services Miami FL",
  description:
    "Professional residential and commercial cleaning services in Miami, FL. Basic, Standard, and Premium cleaning packages available.",
  inLanguage: "en-US",
};

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${siteUrl}/#webpage`,
  url: siteUrl,
  name: "ZoroBabel Cleaning Services Miami FL – Professional Residential & Commercial Cleaning",
  description:
    "ZoroBabel Cleaning Services in Miami, FL offers trusted residential and commercial cleaning. Book deep cleaning, move-in/out, eco-friendly & window cleaning. Licensed, insured & satisfaction guaranteed.",
  isPartOf: { "@id": `${siteUrl}/#website` },
  about: { "@id": `${siteUrl}/#business` },
  inLanguage: "en-US",
  dateModified: "2026-03-24",
};

const serviceSchema = [
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${siteUrl}/#service-basic`,
    serviceType: "Maintenance Cleaning",
    name: "Basic Maintenance Cleaning",
    provider: { "@id": `${siteUrl}/#business` },
    description:
      "Regular maintenance cleaning for homes that are already in good condition. Covers all main areas: surfaces, floors, kitchen, bathrooms, and light organization.",
    areaServed: { "@type": "City", name: "Miami" },
    offers: {
      "@type": "Offer",
      price: "150",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${siteUrl}/#service-standard`,
    serviceType: "Full Cleaning",
    name: "Standard Full Cleaning",
    provider: { "@id": `${siteUrl}/#business` },
    description:
      "Full home cleaning with more attention to detail. Includes detailed bathroom cleaning, appliance exteriors, doors, frames, baseboards, thorough dusting, and inside the microwave.",
    areaServed: { "@type": "City", name: "Miami" },
    offers: {
      "@type": "Offer",
      priceSpecification: {
        "@type": "PriceSpecification",
        minPrice: "220",
        maxPrice: "250",
        priceCurrency: "USD",
      },
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${siteUrl}/#service-premium`,
    serviceType: "Deep Cleaning",
    name: "Premium Deep Cleaning",
    provider: { "@id": `${siteUrl}/#business` },
    description:
      "Thorough deep cleaning ideal for first-time clients or heavily soiled homes. Includes inside oven and refrigerator, interior windows, hard-to-reach areas, grease removal, and special focus on kitchen and bathrooms.",
    areaServed: { "@type": "City", name: "Miami" },
    offers: {
      "@type": "Offer",
      price: "300",
      priceCurrency: "USD",
    },
  },
];

export default function HomePage() {
  return (
    <div className="min-h-screen" style={{ fontFamily: "'Inter', sans-serif" }}>
      <SchemaOrg schema={localBusinessSchema} id="schema-local-business" />
      <SchemaOrg schema={websiteSchema} id="schema-website" />
      <SchemaOrg schema={webPageSchema} id="schema-webpage" />
      <SchemaOrg schema={serviceSchema} id="schema-services" />
      <Navbar />
      <main>
        <HeroBanner />
        <ServicesOverview />
        <WhyChooseUs />
        <Pricing />
        <Gallery />
        <Testimonials />
        <BookingForm />
      </main>
      <Footer />
    </div>
  );
}